import React from "react";
import { Loader2 } from "lucide-react";

/**
 * Sticky liquid-glass Storefront Hunter pagination.
 * Unknown total → "Page N" (never "Page 1 of 1" unless total_pages known).
 * Known total → "1–40 of 171 products" + first/last when total_pages > 1.
 */
export function StorefrontPagination({
  catalogScope = null,
  currentPage = 1,
  pageSize = 40,
  total = null,
  totalPages: totalPagesProp = null,
  from = null,
  to = null,
  visibleCount = 0,
  hasNext: hasNextProp,
  hasPrevious: hasPreviousProp,
  loadingDirection = null,
  onFirst,
  onPrevious,
  onNext,
  onLast,
}) {
  const featured = catalogScope === "featured";
  const page = Math.max(1, Number(currentPage) || 1);
  const size = Math.max(1, Number(pageSize) || 40);
  const totalKnown = Number.isFinite(Number(total)) && Number(total) >= 0;
  const totalNum = totalKnown ? Number(total) : null;

  const totalPages =
    Number.isFinite(Number(totalPagesProp)) && Number(totalPagesProp) > 0
      ? Number(totalPagesProp)
      : totalKnown && size > 0
        ? Math.ceil(totalNum / size)
        : null;

  const canNext =
    hasNextProp === true ||
    (hasNextProp == null && totalPages != null && page < totalPages);

  const canPrevious =
    hasPreviousProp === true || (hasPreviousProp == null && page > 1);

  const hasKnownLastPage = Number.isFinite(totalPages) && totalPages > 1;

  const pageLabel =
    totalPages != null && totalPages > 0
      ? `Page ${page} of ${totalPages}`
      : `Page ${page}`;

  const fromN = Number.isFinite(Number(from)) ? Number(from) : null;
  const toN = Number.isFinite(Number(to)) ? Number(to) : null;

  let rangeLabel;
  if (featured) {
    rangeLabel =
      visibleCount === 1 ? "1 featured product" : `${visibleCount} featured products`;
  } else if (totalKnown && fromN > 0 && toN > 0) {
    rangeLabel = `${fromN}–${toN} of ${totalNum.toLocaleString()} products`;
  } else {
    rangeLabel = `${visibleCount} product${visibleCount === 1 ? "" : "s"} on this page`;
  }

  const busy = Boolean(loadingDirection);

  return (
    <nav
      className="sfh-pagination sfh-pagination-sticky"
      data-testid="sfh-pagination"
      aria-label="Storefront results pages"
    >
      <span className="sfh-pagination-count" data-testid="sfh-pagination-range">
        {rangeLabel}
      </span>

      <div className="sfh-pagination-controls">
        {hasKnownLastPage ? (
          <button
            type="button"
            className="sfh-page-btn"
            disabled={!canPrevious || busy || page <= 1}
            onClick={() => onFirst?.()}
            aria-label="First page"
            data-testid="sfh-pagination-first"
          >
            |‹
          </button>
        ) : null}
        <button
          type="button"
          className="sfh-page-btn"
          disabled={!canPrevious || busy}
          onClick={() => onPrevious?.()}
          aria-label="Previous page"
          data-testid="sfh-pagination-prev"
        >
          {loadingDirection === "previous" ? (
            <Loader2 className="sfh-page-spinner" aria-hidden />
          ) : (
            "‹"
          )}
        </button>
        <span className="sfh-page-pill" data-testid="sfh-pagination-page">
          {pageLabel}
        </span>
        <button
          type="button"
          className="sfh-page-btn"
          disabled={!canNext || busy}
          onClick={() => onNext?.()}
          aria-label="Next page"
          data-testid="sfh-pagination-next"
        >
          {loadingDirection === "next" ? (
            <Loader2 className="sfh-page-spinner" aria-hidden />
          ) : (
            "›"
          )}
        </button>
        {hasKnownLastPage ? (
          <button
            type="button"
            className="sfh-page-btn"
            disabled={!canNext || busy || page >= totalPages}
            onClick={() => onLast?.(totalPages)}
            aria-label="Last page"
            data-testid="sfh-pagination-last"
          >
            ›|
          </button>
        ) : null}
      </div>
    </nav>
  );
}
